import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, from, switchMap, take } from 'rxjs';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';

/**
 * Auth Interceptor
 * Adds Firebase authentication token to all API requests
 * Based on: agent-os/specs/01-user-authentication-sso/spec.md
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Promise that resolves once Firebase Auth has finished restoring session state.
   * Prevents race condition where API calls fire before currentUser is available on refresh.
   */
  private authReady: Promise<void>;

  constructor(private auth: Auth) {
    // Wait for the first auth state emission — this resolves once Firebase
    // has finished checking for a persisted session (whether user or null).
    // Timeout after 5 seconds to prevent blocking requests if Firebase is slow to initialize.
    this.authReady = new Promise<void>(resolve => {
      let resolved = false;
      const unsubscribe = onAuthStateChanged(this.auth, () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        }
      });
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          unsubscribe();
          resolve();
        }
      }, 5000);
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only add token to requests going to our API
    if (!req.url.startsWith(environment.apiUrl)) {
      return next.handle(req);
    }

    // Get Firebase token and add to request
    return from(this.getToken()).pipe(
      take(1),
      switchMap(token => {
        if (token) {
          // Clone request and add Authorization header
          const clonedReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
          return next.handle(clonedReq);
        }

        // No token available, proceed without auth header
        return next.handle(req);
      })
    );
  }

  /**
   * Get current Firebase ID token
   * Waits for auth initialization on first call (handles page refresh race condition)
   * Automatically refreshes token if expired
   */
  private async getToken(): Promise<string | null> {
    // Wait for Firebase to finish restoring session before checking currentUser
    await this.authReady;

    const user = this.auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      // Get token, will automatically refresh if expired
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting Firebase token:', error);
      return null;
    }
  }
}
